/*
 * THE PAN/ZOOM CAMERA, ONCE.
 *
 * Three surfaces already draw a graph on a pannable, zoomable stage with a minimap in the corner —
 * the fleet map, the flow canvas, and the agent pipes — and the flow canvas says so in its own
 * comment: "the fleet-map / ai-worker minimap pattern, verbatim". Verbatim is the problem. Each copy
 * has its own clamp, its own zoom-about-a-point arithmetic and its own fit, and a fix to one reaches
 * none of the others. The agent canvas needed zoom and a minimap, which would have made four.
 *
 * PURE FUNCTIONS OVER A CAMERA VALUE, not a base class. The three hosts differ in everything except
 * this arithmetic: different node types, layouts, styling and lifecycles. Sharing a component would
 * force those together; sharing the maths shares exactly the part that is genuinely identical, and
 * leaves each surface to draw itself. It is also the part worth testing, and pure functions are the
 * only shape of it that can be tested without a DOM.
 */
/** Zoom bounds. Below the floor a graph is unreadable, above the ceiling a card fills the stage —
 *  both are places a person can only get to by accident, and cannot get out of by scrolling back. */
export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 4;
export const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
/**
 * Zoom keeping the graph point under (cx, cy) — container-relative pixels — pinned.
 *
 * This is the whole reason wheel-zoom feels right rather than lurching: the thing under the cursor
 * stays under the cursor. Returns the camera unchanged when the clamp bites, so a caller can tell
 * "nothing moved" without comparing floats.
 */
export function zoomAt(cam, cx, cy, factor) {
    const z1 = clampZoom(cam.zoom * factor);
    if (z1 === cam.zoom)
        return cam;
    return {
        zoom: z1,
        panX: cx - ((cx - cam.panX) / cam.zoom) * z1,
        panY: cy - ((cy - cam.panY) / cam.zoom) * z1,
    };
}
/** Step around the viewport centre — what a zoom BUTTON does, as against the wheel. */
export const zoomBy = (cam, e, factor) => zoomAt(cam, e.contW / 2, e.contH / 2, factor);
/**
 * Frame the whole graph, centred, with a margin.
 *
 * The 0.92 leaves the outermost cards clear of the edges: a graph fitted to exactly the viewport
 * touches all four sides and reads as clipped even though every node is visible.
 */
/**
 * Frame the whole graph, centred.
 *
 * `maxZoom` is what stops a SMALL graph being blown up to fill the stage. Two cards in a wide panel
 * fit at 2.4x, at which a card is the size of a dialog and the canvas reads as a zoomed-in detail
 * view of something the reader has not chosen to zoom into. Passing 1 keeps every card at the size
 * it was drawn for and simply centres the graph in the space, which is what "fit" means when the
 * content is already smaller than the frame.
 */
export function fit(e, maxZoom = MAX_ZOOM) {
    if (!e.contW || !e.contH || e.w <= 0 || e.h <= 0)
        return null;
    const zoom = Math.min(maxZoom, clampZoom(Math.min(e.contW / e.w, e.contH / e.h) * 0.92));
    return { zoom, panX: (e.contW - e.w * zoom) / 2, panY: (e.contH - e.h * zoom) / 2 };
}
/** Move the camera by a pointer delta. Panning does not scale: a drag moves the graph under the
 *  finger one-to-one at every zoom, which is what makes it feel like a surface rather than a dial. */
export const panBy = (cam, dx, dy) => ({ ...cam, panX: cam.panX + dx, panY: cam.panY + dy });
/**
 * The viewport rectangle in GRAPH units — what the minimap shades to say "you are here".
 *
 * UNCLAMPED, and that is the whole point of it. It used to be clipped to the graph's own extent,
 * which quietly broke the one thing a minimap has to be true about: the rectangle stopped being the
 * viewport and became "the part of the graph you can see". Zoom out past the graph and it froze at
 * the graph's size instead of growing; make the stage tall and narrow and it kept the graph's
 * proportions instead of the screen's. The map and the canvas then disagreed about how big the
 * world is, which is the only disagreement a map cannot survive.
 *
 * The rectangle is the stage, in graph units, wherever the camera happens to be. A minimap that
 * draws it CLIPS it — the box has overflow hidden, exactly as the flow canvas's does — so a
 * viewport bigger than the graph shows as a frame running off the edges, which is the honest
 * picture of being zoomed out past your own content.
 */
export function viewportRect(cam, e) {
    return { x: -cam.panX / cam.zoom, y: -cam.panY / cam.zoom, w: e.contW / cam.zoom, h: e.contH / cam.zoom };
}
/** Centre the camera on a graph point — what clicking or scrubbing the minimap does. */
export const centreOn = (cam, e, gx, gy) => ({
    ...cam,
    panX: e.contW / 2 - gx * cam.zoom,
    panY: e.contH / 2 - gy * cam.zoom,
});
/*
 * ── Moving the camera, rather than jumping it ──────────────────────────────
 *
 * A cut from one framing to another asks the reader to re-find where they are; a move carries them,
 * and the spatial relationship between the two — parent over here, the child it spawned over there —
 * survives the transition instead of having to be reconstructed afterwards. That is the whole reason
 * the journey is animated: not decoration, but keeping the graph's shape legible while the view
 * changes which part of it you are looking at.
 *
 * Pure, so the easing and the interpolation can be asserted without a canvas, a rAF or a clock.
 */
/** Cubic ease-in-out — slow at both ends, quick through the middle. The app's motion curve. */
export const easeInOutCubic = (t) => {
    const x = Math.min(1, Math.max(0, t));
    return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
};
/**
 * A camera part-way between two.
 *
 * ZOOM IS INTERPOLATED GEOMETRICALLY, not linearly. Zoom is a scale factor, so the perceptually even
 * step between 0.5 and 2 is ×2 then ×2 — not +0.75 then +0.75, which spends most of the animation
 * crawling through the low end and then lurches. Pan is linear because it is a distance.
 */
/**
 * Cubic ease-IN — starts still and accelerates, with no settle at the end.
 *
 * For a movement that is not going to stop, and offered to `lerpCamera` as an alternative to the
 * app's own symmetric curve. Nothing in the agent canvas uses it today: that flight is one
 * continuous arc under one symmetric ease, and a per-leg curve is precisely what used to make it
 * stop dead at every waypoint.
 */
export const easeInCubic = (t) => {
    const x = Math.min(1, Math.max(0, t));
    return x * x * x;
};
export function lerpCamera(a, b, t, ease = easeInOutCubic) {
    const k = ease(t);
    return {
        zoom: a.zoom * Math.pow(b.zoom / a.zoom, k),
        panX: a.panX + (b.panX - a.panX) * k,
        panY: a.panY + (b.panY - a.panY) * k,
    };
}
/** The camera that puts a graph point in the middle at a given zoom — the target of a fly-to. */
export const framedOn = (e, gx, gy, zoom) => ({
    zoom: clampZoom(zoom),
    panX: e.contW / 2 - gx * clampZoom(zoom),
    panY: e.contH / 2 - gy * clampZoom(zoom),
});
//# sourceMappingURL=camera.js.map